import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { authFetch } from '../services/authFetch';
import CallRateCalculator from '../components/CallRateCalculator';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../context/ToastContext';
import { CreditCard } from 'lucide-react';
import { logger } from '../services/logger';
import SEO from '../components/SEO';

const BuyCreditsPage = () => {
  const { token, login, logout } = useAuth();
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedAmount, setSelectedAmount] = useState(25);
  const [customAmount, setCustomAmount] = useState('');
  const navigate = useNavigate();
  const { addToast } = useToast();
  const multiplier = 50;

  const creditPackages = [
    { amount: 10, label: '$10', bonusText: '' },
    { amount: 25, label: '$25', bonusText: '' },
    { amount: 50, label: '$50', bonusText: '5% free' },
    { amount: 100, label: '$100', bonusText: '10% free' }
  ];

  const handleAmountSelect = (amount) => {
    setSelectedAmount(amount);
    setCustomAmount('');
  };

  const handleCustomAmountChange = (e) => {
    if (e.target.value < 0) {
      addToast('warning', 'Minimum custom amount is $5');
      return;
    }
    if (e.target.value >= 4001) {
      addToast('warning', 'Maximum custom amount is $4000');
      return;
    }
    setCustomAmount(e.target.value);
    setSelectedAmount(null);
  };

  const handleCheckout = async () => {
    if (!token) {
      addToast('success', 'Sign up to buy credits');
      navigate('/register');
      return;
    }
    const amount = customAmount ? parseFloat(customAmount) : selectedAmount;
    
    if (!amount || amount < 5) {
      addToast('warning', 'Please select or enter a valid amount (minimum $5)');
      return;
    }

    try {
      setLoading(true);
      const totalCredits = getBonusCredits(amount);

      const res = await fetch(`${import.meta.env.VITE_BASE_URL}/api/payments/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ 
          amount: amount,
          credits: totalCredits
        })
      });
  
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url; // Redirect to Stripe checkout
      } else {
        logger.warn("Stripe couldn't create checkout session");
        addToast('error', 'Could not create checkout session');
      }
    } catch (error) {
      logger.error(error, 'Checkout error:');
      addToast('error','An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getBonusPercentage = (amount) => {
    if (amount === 50) return 0.05;
    if (amount === 100) return 0.10;
    return 0;
  };
  
  const getMinutes = (amount) => Math.floor(amount * multiplier);
  
  const getBonusMinutesText = (amount) => {
    const bonusPercent = getBonusPercentage(amount);
    if (!bonusPercent) return null;
  
    const bonusAmount = amount * bonusPercent;
    const bonusMinutes = getMinutes(bonusAmount);
  
    return `+ ${bonusMinutes} minutes free!`;
  };

  const getBonusCredits = (amount) => {
    const bonus = getBonusPercentage(amount);
    return Math.floor(amount * (1 + bonus));
  };

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await authFetch(
          `${import.meta.env.VITE_BASE_URL}/api/protected/me`,
          {},
          { token, login, logout }
        );

        if (res && res.ok) {
          const data = await res.json();
          setUserInfo(data.user);
        } else {
          setUserInfo(null);
        }
      } catch (error) {
        logger.error(error, 'Error fetching user');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  
  // Calculate estimated minutes based on selected amount
  const estimatedMinutes = selectedAmount ? Math.floor(selectedAmount * multiplier) : 
                           customAmount ? Math.floor(parseFloat(customAmount || 0) * multiplier) : 0;

  return (
    <>
      <SEO
        title="Buy Call Credits | DialVia"
        description="Top up your DialVia credits to make affordable international calls. Choose preset amounts or enter your own custom value."
        canonical="https://dialvia.com/buy-credits"
      />
    <div className="max-w-screen-xl mx-auto">
    <div className="flex flex-col md:flex-row gap-6 items-stretch">
      {/* Left column - Credit purchase */}
      <div className="flex-grow basis-0 shadow-xl rounded-lg p-6 bg-white border border-purple-100">
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-4">
            <CreditCard className="text-purple-600" />
            <h2 className="text-xl font-bold text-gray-900">Select Your Credit Amount</h2>
          </div>
          
          <p className="text-gray-700 mb-3">
            Use your credits to call over <span className='text-purple-600'>150</span> countries worldwide.
          </p>
          
          <div className="mt-6">
            <p className="mb-2 text-gray-900">Choose Amount (USD)</p>
            <div className="grid gap-2 grid-cols-[repeat(auto-fit,minmax(75px,1fr))]">
              {creditPackages.map((pkg) => (
                <button
                  key={pkg.amount}
                  className={`relative p-4 rounded-md border transition-colors ${
                    selectedAmount === pkg.amount 
                      ? 'bg-purple-100 border-purple-500' 
                      : 'bg-gray-50 border-gray-300 hover:bg-purple-50'
                  }`}
                  onClick={() => handleAmountSelect(pkg.amount)}
                >
                  {pkg.bonusText && (
                    <span className="absolute -top-2 -right-2 text-xs bg-purple-500 text-white rounded-full px-2 py-1 shadow-md">
                      {pkg.bonusText}
                    </span>
                  )}
                  <p className="text-lg font-bold text-gray-900">{pkg.label}</p>
                </button>
              ))}
            </div>
          </div>
          
          <div className="mt-6">
            <p className="text-gray-900">Or enter custom amount (minimum $5)</p>
            <div className="mt-2">
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  min="10"
                  max="4000"
                  value={customAmount}
                  onChange={handleCustomAmountChange}
                  className="w-full p-3 pl-8 bg-white border border-purple-200 rounded-md text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-300"
                  placeholder="25"
                />
              </div>
            </div>
          </div>
  
          <div className="mt-6 bg-purple-50 p-4 rounded-md flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 border-l-4 border-purple-500">
            <p className="text-lg text-gray-900">
              Get up to <span className="text-purple-600 font-bold">{estimatedMinutes}</span> minutes of international calling time
              {selectedAmount && getBonusMinutesText(selectedAmount) && (
                <span className="text-purple-600 font-medium"> {getBonusMinutesText(selectedAmount)}</span>
              )}
            </p>
          </div>
  
          <button
            onClick={handleCheckout}
            disabled={loading || (!selectedAmount && !customAmount)}
            className="mt-6 w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-4 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Processing...' : 'Secure Checkout'}
          </button>
        </div>
      </div>
  
      {/* Right column - Benefits */}
      <div id='calculator' className="md:w-96 flex flex-col h-full">
        <CallRateCalculator />
      </div>
    </div>
  </div>
  </>
  );  
};

export default BuyCreditsPage;